import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { RoleDto } from './roles.service';

export interface UserWithRolesDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  isActive: boolean;
  roles: RoleDto[];
  permissions: string[];
}

export interface UserListDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  isActive: boolean;
  roleNames: string[];
}

export interface UserFilterDto {
  search?: string;
  roleId?: string;
  page: number;
  pageSize: number;
}

export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface AssignRolesDto {
  userId: string;
  roleIds: string[];
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  roleIds?: string[];
}

export interface UpdateUserDto {
  firstName: string;
  lastName: string;
  email?: string;
}

export interface ResetUserPasswordDto {
  newPassword: string;
  sendEmail?: boolean;
}

export interface UserCreatedResponseDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UserRolesService {
  private readonly apiUrl = `${environment.apiUrl}/userroles`;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene todos los usuarios con sus roles (paginado)
   */
  getUsersWithRoles(filter: UserFilterDto): Observable<PagedResult<UserListDto>> {
    let params = new HttpParams()
      .set('page', filter.page.toString())
      .set('pageSize', filter.pageSize.toString());

    if (filter.search) {
      params = params.set('search', filter.search);
    }
    if (filter.roleId) {
      params = params.set('roleId', filter.roleId);
    }

    return this.http.get<PagedResult<UserListDto>>(`${this.apiUrl}/users`, { params });
  }

  /**
   * Obtiene un usuario con todos sus roles y permisos
   */
  getUserWithRoles(userId: string): Observable<UserWithRolesDto> {
    return this.http.get<UserWithRolesDto>(`${this.apiUrl}/user/${userId}`);
  }

  /**
   * Obtiene los roles del usuario actual
   */
  getMyRoles(): Observable<RoleDto[]> {
    return this.http.get<RoleDto[]>(`${this.apiUrl}/my-roles`);
  }

  /**
   * Obtiene los permisos del usuario actual
   */
  getMyPermissions(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/my-permissions`);
  }

  /**
   * Asigna roles a un usuario (reemplaza los existentes)
   */
  assignRolesToUser(userId: string, roleIds: string[]): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/assign`, { userId, roleIds });
  }

  /**
   * Agrega un rol a un usuario
   */
  addRoleToUser(userId: string, roleId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/user/${userId}/role/${roleId}`, {});
  }

  /**
   * Remueve un rol de un usuario
   */
  removeRoleFromUser(userId: string, roleId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/user/${userId}/role/${roleId}`);
  }

  /**
   * Verifica si el usuario actual tiene un permiso específico
   */
  hasPermission(permissionCode: string): Observable<{ hasPermission: boolean }> {
    return this.http.get<{ hasPermission: boolean }>(`${this.apiUrl}/has-permission/${permissionCode}`);
  }

  // ==================== GESTIÓN DE USUARIOS ====================

  /**
   * Crea un nuevo usuario
   */
  createUser(user: CreateUserDto): Observable<UserCreatedResponseDto> {
    return this.http.post<UserCreatedResponseDto>(`${this.apiUrl}/create`, user);
  }

  /**
   * Actualiza la información de un usuario
   */
  updateUser(userId: string, user: UpdateUserDto): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/user/${userId}`, user);
  }

  /**
   * Reinicia la contraseña de un usuario
   */
  resetUserPassword(userId: string, data: ResetUserPasswordDto): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/user/${userId}/reset-password`, data);
  }

  /**
   * Activa o desactiva un usuario (solo SuperAdmin).
   * Los usuarios desactivados no pueden iniciar sesión.
   */
  setUserActive(userId: string, isActive: boolean): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.apiUrl}/user/${userId}/active`, { isActive });
  }
}

